import {
    AccountInfo,
    AuthenticationResult,
    BrowserAuthError,
    BrowserAuthErrorCodes,
    IPublicClientApplication,
    InteractionRequiredAuthError,
    PublicClientApplication,
    createNestablePublicClientApplication,
} from "@azure/msal-browser";
import { Logger } from "../Logger";
import { AnalyticsActionType, AnalyticsCategoryType, AnalyticsManager } from "../AnalyticsManager";

/** The only delegated Graph permissions this feature ever requests; the consent dialog rejects anything else. */
export const SHAREPOINT_GRAPH_SCOPES: ReadonlyArray<string> = ["Sites.Read.All"];

/**
 * Entra identifiers for one SharePoint connection. `clientId`/`authority` come from the project's
 * `/settings` response (`ProjectFolderConnection.appId` / `tenantId`), not from build-time config,
 * so MSAL clients are created lazily per connection (see SharePointAuthServiceFactory).
 */
export interface SharePointMsalClientOptions {
    clientId: string;
    authority: string;
    /** Required for the Office Dialog fallback (redirect leg); unused under NAA. */
    redirectUri?: string;
}

/**
 * Builds the (uninitialized) MSAL client for a SharePoint connection's Entra app. Injected into
 * `SharePointAuthService` so tests can use a fake/mock `IPublicClientApplication` instead of
 * exercising real MSAL/crypto.
 */
export function createSharePointMsalClient(options: SharePointMsalClientOptions): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: options.clientId,
            authority: options.authority,
            redirectUri: options.redirectUri,
        },
        system: {
            // The SharePoint consent flow runs the redirect inside an Office Dialog
            // (see SharePointConsentDialog/SharePointConsentInitiateComponent), which MSAL's
            // default iframe-detection heuristic can mistake for an untrusted embedded iframe,
            // throwing before ever navigating to the login page. This flow is a first-party,
            // same-origin bounce page we control, so it's safe to allow.
            allowRedirectInIframe: true,
        },
    });
}

/**
 * Builds a "nestable" MSAL client using Nested App Authentication (NAA) — see
 * doc/npc-32337-sharepoint-msal-naa-auth.md for the full rationale. Unlike
 * `createSharePointMsalClient`, this runs entirely inside the taskpane and
 * lets the Office host broker sign-in natively, so it needs neither `redirectUri` (there's no
 * redirect leg) nor `allowRedirectInIframe` (there's no iframe-detection heuristic to trip).
 *
 * NAA is only usable on hosts that support the `NestedAppAuth` requirement set —
 * `SharePointAuthServiceFactory` checks `OfficeWrapper.isNestedAppAuthSupported()` and falls back
 * to `createSharePointMsalClient` + `SharePointConsentDialog` otherwise; `EntryPoint.tsx` uses the
 * same flag to gate its redirect-leg bootstrap.
 */
export async function createNestableSharePointMsalClient(
    options: SharePointMsalClientOptions
): Promise<IPublicClientApplication> {
    return createNestablePublicClientApplication({
        auth: {
            clientId: options.clientId,
            authority: options.authority,
        },
    });
}

/** Which interactive path a SharePointAuthService was wired with; only used to label log lines for QA. */
export type SharePointAuthPath = "MSAL + NAA" | "MSAL-only";

/** Reason an interactive/silent SharePoint token acquisition ultimately failed. */
export type SharePointAuthErrorReason = "declined" | "network" | "unknown";

/** Typed error surfaced by SharePointAuthService so callers don't need to know about MSAL's error types. */
export class SharePointAuthError extends Error {
    constructor(message: string, public readonly reason: SharePointAuthErrorReason) {
        super(message);
        // Required for correct instanceof checks when TypeScript targets ES5.
        Object.setPrototypeOf(this, SharePointAuthError.prototype);
    }
}

/**
 * Performs the interactive (consent) step of acquiring a SharePoint Graph token.
 * Implemented by a hosted Office Dialog API flow (see SharePointConsentDialog) so
 * that the popup works inside the sandboxed Outlook taskpane iframe.
 */
export interface ISharePointInteractiveAuthProvider {
    acquireTokenInteractive(scopes: string[]): Promise<AuthenticationResult>;
}

/**
 * Wraps an @azure/msal-browser `IPublicClientApplication` to acquire Microsoft Graph
 * access tokens for browsing SharePoint Online project folders, using a dedicated Entra
 * app registration (separate from the existing server-mediated Graph token flow used for
 * Outlook mail folder access). The MSAL client itself is constructed by the composition
 * root via `createSharePointMsalClient` and injected here, keeping this class testable.
 *
 * First-time vs. returning-user detection is MSAL's standard SPA pattern: always try
 * `acquireTokenSilent` first when there's an active account (or, under NAA, even without one -
 * the Office host brokers it for the signed-in mailbox user), and only fall back to the
 * interactive prompt when it throws `InteractionRequiredAuthError` (or the
 * `redirect_bridge_timeout` equivalent — see `requiresInteractionFallback`). There used to be an
 * additional manually-persisted "hasConsented" flag gating this (an interim stand-in for easier
 * manual testing, per SPEC.md FR-5); it was removed since it never changed the outcome
 * `acquireTokenSilent`'s own error handling wouldn't have produced on its own.
 */
export class SharePointAuthService {
    private initializePromise: Promise<void> | null = null;
    /**
     * Single-flight guard for the interactive consent flow. The first browse issues several Graph
     * requests in quick succession (site resolution, then folder listing), and in dev React also
     * double-invokes effects — so multiple callers can reach the interactive path before the first
     * has persisted consent. Without this, each would open its own Office consent dialog. Callers
     * share the in-flight promise so at most one dialog is ever open.
     */
    private interactiveTokenPromise: Promise<string> | null = null;
    /**
     * In-memory cache of the most recently acquired Graph token. This is the primary token-reuse
     * path: the interactive token is minted in the separate Office Dialog browsing context, whose
     * MSAL cache the taskpane's own PublicClientApplication instance cannot reliably read back
     * (Office storage partitioning) — so acquireTokenSilent here can miss even immediately after a
     * successful interactive sign-in, which would otherwise open a second consent dialog within a
     * single browse. Caching the token here lets the several Graph requests of one browse (and
     * repeat browses within its lifetime) share a single acquisition.
     */
    private cachedToken: { accessToken: string; expiresOnMs: number } | null = null;
    /** Set by invalidateCachedToken; MSAL's own cache would otherwise hand back the same rejected token. */
    private forceRefreshNext = false;
    /** Refresh a little before the real expiry to avoid handing out a token that expires mid-request. */
    private readonly tokenExpirySkewMs = 60_000;

    constructor(
        private msalClient: IPublicClientApplication,
        private interactiveAuthProvider: ISharePointInteractiveAuthProvider,
        private logger: Logger,
        private readonly scopes: string[] = [...SHAREPOINT_GRAPH_SCOPES],
        private analyticsManager?: AnalyticsManager,
        /** Entra tenant this service's authority targets; cached accounts from other tenants are ignored. */
        private readonly tenantId?: string,
        private readonly authPath: SharePointAuthPath | "MSAL" = "MSAL"
    ) {}

    private get logPrefix(): string {
        return `SharePointAuthService [${this.authPath}]`;
    }

    /** Call after a Graph 401: drops the in-memory token and makes the next silent request skip MSAL's cache too. */
    invalidateCachedToken(): void {
        this.cachedToken = null;
        this.forceRefreshNext = true;
    }

    /**
     * Returns a Microsoft Graph access token with the scopes needed to browse SharePoint
     * project folders, prompting for interactive consent via `interactiveAuthProvider` when
     * needed (first-time use, or when silent acquisition fails).
     */
    async getAccessToken(): Promise<string> {
        await this.ensureInitialized();

        // Reuse a still-valid token from a prior acquisition first. This avoids a second consent
        // dialog when acquireTokenSilent below can't see the token minted in the Office Dialog's
        // separate MSAL cache (see cachedToken docs).
        const cached = this.getCachedToken();
        if (cached) {
            this.logger.info(
                `${this.logPrefix}: token source = in-memory cache (no MSAL call, token NOT refreshed) - ` +
                    SharePointAuthService.describeExpiry(this.cachedToken!.expiresOnMs)
            );
            return cached;
        }

        const account = this.getActiveAccount();

        // NAA brokers silent requests for the signed-in mailbox user without a cached account; a fresh
        // task pane (every open on mobile) has none, so skipping silent here would prompt on every visit.
        const canAttemptSilent = !!account || this.authPath === "MSAL + NAA";
        if (!canAttemptSilent) {
            this.logger.info(
                `${this.logPrefix}: no MSAL account cached for tenant ${this.tenantId ?? "(any)"} - ` +
                    "silent acquisition impossible, interactive sign-in required"
            );
            return this.acquireTokenInteractive();
        }

        try {
            const forceRefresh = this.forceRefreshNext;
            this.forceRefreshNext = false;
            this.logger.info(
                `${this.logPrefix}: calling pca.acquireTokenSilent(scopes=[${this.scopes.join(
                    ", "
                )}], forceRefresh=${forceRefresh}) for ${
                    account
                        ? `the cached account in tenant ${account.tenantId}`
                        : "the host's signed-in user (no cached MSAL account; NAA broker resolves it)"
                }`
            );
            const result = await this.msalClient.acquireTokenSilent({ scopes: this.scopes, account, forceRefresh });
            if (!account && result.account) {
                this.msalClient.setActiveAccount(result.account);
            }
            const expiresOnMs = this.cacheToken(result);
            this.logger.info(
                `${this.logPrefix}: token source = acquireTokenSilent, ${this.describeSilentRefresh(result)} - ` +
                    SharePointAuthService.describeExpiry(expiresOnMs)
            );
            return result.accessToken;
        } catch (error) {
            if (
                error instanceof InteractionRequiredAuthError ||
                SharePointAuthService.requiresInteractionFallback(error)
            ) {
                this.logger.info(
                    `${this.logPrefix}: acquireTokenSilent could not refresh silently (${(error as any)?.errorCode ??
                        "interaction_required"}) - falling back to interactive sign-in`
                );
                return this.acquireTokenInteractive();
            }
            if (!account) {
                // Account-less NAA bootstrap used to go straight to interactive; keep that as the worst case.
                this.logger.warning(
                    `${this.logPrefix}: account-less acquireTokenSilent failed (${(error as any)?.errorCode ??
                        "unknown"}) - falling back to interactive sign-in`,
                    error
                );
                return this.acquireTokenInteractive();
            }
            this.logger.error(`${this.logPrefix}: acquireTokenSilent failed with a non-interactive error`, error);
            throw new SharePointAuthError("Failed to silently acquire a SharePoint access token", "network");
        }
    }

    /**
     * True for silent-acquisition failures that, in this app's context, mean "silent isn't
     * possible here" rather than a real network/config failure — so they should fall back to
     * the interactive dialog just like InteractionRequiredAuthError, instead of surfacing as an
     * error with no popup ever shown.
     *
     * Specifically: msal-browser v5's `acquireTokenSilent` falls back to a hidden iframe (the
     * same "redirect bridge" mechanism used by `ssoSilent`/popups) when it needs to silently
     * renew a token. That bridge uses `BroadcastChannel` to relay the response back to the app,
     * which requires the app and the hidden iframe to share a storage partition. The Outlook
     * taskpane is itself a cross-origin iframe, so it's in a different partition than the hidden
     * iframe MSAL creates — the broadcast is never received, and the call always fails with
     * `BrowserAuthError("timed_out", subError: "redirect_bridge_timeout")`, never with
     * InteractionRequiredAuthError. See https://aka.ms/msal.js.errors#timed_out and
     * lib/msal-browser/docs/redirect-bridge.md in the msal-browser repo.
     */
    private static requiresInteractionFallback(error: unknown): boolean {
        return (
            error instanceof BrowserAuthError &&
            error.errorCode === BrowserAuthErrorCodes.timedOut &&
            error.subError === "redirect_bridge_timeout"
        );
    }

    private async acquireTokenInteractive(): Promise<string> {
        // Coalesce concurrent interactive requests onto a single dialog (see interactiveTokenPromise).
        if (this.interactiveTokenPromise) {
            this.logger.info(
                `${this.logPrefix}: an interactive sign-in is already in flight, ` +
                    "coalescing onto it instead of starting another"
            );
            return this.interactiveTokenPromise;
        }
        this.interactiveTokenPromise = this.doAcquireTokenInteractive();
        try {
            return await this.interactiveTokenPromise;
        } finally {
            this.interactiveTokenPromise = null;
        }
    }

    private async doAcquireTokenInteractive(): Promise<string> {
        let result: AuthenticationResult;
        try {
            this.logger.info(
                `${this.logPrefix}: calling interactiveAuthProvider.acquireTokenInteractive(scopes=[${this.scopes.join(
                    ", "
                )}]) - handing off to the ${this.authPath} interactive provider`
            );
            result = await this.interactiveAuthProvider.acquireTokenInteractive(this.scopes);
            this.analyticsManager?.recordEvent(
                AnalyticsCategoryType.UserActions,
                AnalyticsActionType.SharePointConsentCompleted
            );
        } catch (error) {
            this.logger.error(`${this.logPrefix}: interactive SharePoint sign-in failed`, error);
            this.analyticsManager?.recordEvent(
                AnalyticsCategoryType.UserActions,
                AnalyticsActionType.SharePointConsentFailed
            );
            throw new SharePointAuthError("User declined or failed to complete SharePoint consent", "declined");
        }

        if (result.account) {
            this.msalClient.setActiveAccount(result.account);
        }
        const expiresOnMs = this.cacheToken(result);
        this.logger.info(
            `${this.logPrefix}: token source = interactive sign-in via ${this.authPath} (fromCache=${result.fromCache}) - ` +
                `account for tenant ${result.account?.tenantId ?? "(unknown)"} set active - ` +
                SharePointAuthService.describeExpiry(expiresOnMs)
        );
        return result.accessToken;
    }

    /**
     * `fromCache=true` always means MSAL's own cache. `fromCache=false` means a refresh on MSAL-only
     * (refresh-token grant), but under NAA msal-browser hard-codes it for every bridge response, so the
     * Office host may have served its own cached token - no refresh can be inferred there.
     */
    private describeSilentRefresh(result: AuthenticationResult): string {
        if (result.fromCache) {
            return "token refreshed = false (fromCache=true: MSAL returned the token already in its own cache)";
        }
        return this.authPath === "MSAL + NAA"
            ? "token refreshed = unknown (fromCache=false: served by the Office host broker; MSAL cannot tell broker cache from a new Entra token)"
            : "token refreshed = true (fromCache=false: new token issued by Entra via the refresh token)";
    }

    /** Returns the in-memory token if it exists and isn't within the expiry skew of expiring, else null. */
    private getCachedToken(): string | null {
        if (this.cachedToken && this.cachedToken.expiresOnMs - Date.now() > this.tokenExpirySkewMs) {
            return this.cachedToken.accessToken;
        }
        return null;
    }

    /** Stores a freshly acquired token in memory, keyed by its expiry (defaults to 5 min if MSAL omits one). */
    private cacheToken(result: AuthenticationResult): number {
        const expiresOnMs = SharePointAuthService.toExpiryMs(result);
        this.cachedToken = { accessToken: result.accessToken, expiresOnMs };
        return expiresOnMs;
    }

    private static describeExpiry(expiresOnMs: number): string {
        const minutes = Math.round((expiresOnMs - Date.now()) / 60_000);
        return `expires at ${new Date(expiresOnMs).toISOString()} (in ${minutes} min)`;
    }

    /**
     * Normalizes an AuthenticationResult's expiry to epoch millis. On the interactive path the
     * result has been JSON round-tripped through the Office Dialog's messageParent, so `expiresOn`
     * is actually an ISO string at runtime (not the `Date` its TS type claims); on the silent path
     * it's a real `Date`. Handle both (and a numeric epoch), falling back to 5 minutes out.
     */
    private static toExpiryMs(result: AuthenticationResult): number {
        const raw: unknown = result.expiresOn;
        if (raw instanceof Date) {
            return raw.getTime();
        }
        if (typeof raw === "string") {
            const parsed = Date.parse(raw);
            if (!Number.isNaN(parsed)) {
                return parsed;
            }
        }
        if (typeof raw === "number") {
            return raw;
        }
        return Date.now() + 5 * 60_000;
    }

    private getActiveAccount(): AccountInfo | undefined {
        // MSAL's cache is per clientId, not per authority, so the same app used in two tenants shares one
        // account list; picking another tenant's account would make acquireTokenSilent fail or mis-target.
        // GUIDs are case-insensitive; NPC and MSAL need not agree on casing.
        const expectedTenantId = this.tenantId?.toLowerCase();
        const matchesTenant = (account: AccountInfo) =>
            !expectedTenantId || account.tenantId?.toLowerCase() === expectedTenantId;
        const active = this.msalClient.getActiveAccount();
        if (active && matchesTenant(active)) {
            return active;
        }
        return this.msalClient.getAllAccounts().find(matchesTenant);
    }

    private async ensureInitialized(): Promise<void> {
        if (!this.initializePromise) {
            this.logger.info(`${this.logPrefix}: pca.initialize() - first token request on this MSAL client`);
            this.initializePromise = this.msalClient.initialize();
        }
        await this.initializePromise;
    }
}
